import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { FilterRow, TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty, isPieceUom } from '../lib/quantity';
import { purchaseTypeLabel } from '../lib/purchaseLabels';
import {
  purchaseDocumentsV2Api,
  type PurchaseDocumentV2FormData,
  type PurchaseDocumentV2ListItem,
  type PurchaseDocumentsV2Meta,
} from '../services/purchaseDocumentsV2';

type FormRow = {
  rowId?: number;
  containerId?: number | null;
  containerCode: string;
  productId: number | '';
  productSearch: string;
  qty: string;
  uomItemId: number | '';
  siteId: number | '';
  waterPercent: string;
  notes: string;
};

const EMPTY_FORM: PurchaseDocumentV2FormData = {
  purchaseType: 'HONEY',
  supplierId: null,
  documentDate: '',
  notes: '',
  rows: [],
};

const EMPTY_ROW: FormRow = {
  containerCode: '',
  productId: '',
  productSearch: '',
  qty: '',
  uomItemId: '',
  siteId: '',
  waterPercent: '',
  notes: '',
};

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function productOptionLabel(product: PurchaseDocumentsV2Meta['products'][number]): string {
  return product.sku ? `${product.sku} - ${product.name}` : product.name;
}

function isBulkHoneyOrPollen(meta: PurchaseDocumentsV2Meta, productId: number | ''): boolean {
  if (productId === '') return false;
  const product = meta.products.find((item) => item.id === Number(productId));
  const category = (product?.categoryLabel ?? '').trim().toLowerCase();
  return product?.kind === 'BULK' && (category === 'honung' || category === 'pollen');
}

function normalizeLocalizedDecimal(value: string): string {
  return value.replace(',', '.');
}

function purchaseTypeNeedsQualityFields(purchaseType: string): boolean {
  return purchaseType === 'HONEY' || purchaseType === 'POLLEN';
}

function documentYear(value: string): number | null {
  const year = Number(String(value).slice(0, 4));
  return Number.isFinite(year) && year > 0 ? year : null;
}

function nextDocumentNumberForYear(documents: PurchaseDocumentV2ListItem[], year: number): string {
  const shortYear = String(year).slice(-2);
  let maxNumber = 0;
  for (const document of documents) {
    const match = String(document.documentNumber).match(new RegExp(`^I${shortYear}\\.(\\d+)$`));
    if (!match) continue;
    maxNumber = Math.max(maxNumber, Number(match[1]));
  }
  return `I${shortYear}.${maxNumber + 1}`;
}

export function ReceivingRebuildPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { canRead } = useAuth();
  const currentYear = new Date().getFullYear();
  const [meta, setMeta] = useState<PurchaseDocumentsV2Meta | null>(null);
  const [documents, setDocuments] = useState<PurchaseDocumentV2ListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<PurchaseDocumentV2FormData>(EMPTY_FORM);
  const [rows, setRows] = useState<FormRow[]>([]);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [openProductSearchIndex, setOpenProductSearchIndex] = useState<number | null>(null);

  if (!canRead('receiving')) {
    return <Navigate to="/" replace />;
  }

  const defaultSiteId = meta?.sites.find((site) => site.name === 'Lager')?.id ?? meta?.sites[0]?.id ?? '';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [metaRes, listRes] = await Promise.all([purchaseDocumentsV2Api.meta(), purchaseDocumentsV2Api.list()]);
      setMeta(metaRes);
      setDocuments(listRes.documents);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda inköpsordrar');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const filteredDocuments = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return documents.filter((document) => {
      const matchesYear = documentYear(document.documentDate) === selectedYear;
      const matchesText =
        !term ||
        document.documentNumber.toLowerCase().includes(term) ||
        (document.supplierName ?? '').toLowerCase().includes(term);
      const matchesType = !typeFilter || document.purchaseType === typeFilter;
      return matchesYear && matchesText && matchesType;
    });
  }, [documents, filter, selectedYear, typeFilter]);

  const nextDocumentNumber = useMemo(() => nextDocumentNumberForYear(documents, selectedYear), [documents, selectedYear]);

  const openCreate = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    setForm({
      ...EMPTY_FORM,
      documentDate: `${selectedYear}-${month}-${day}`,
      purchaseType: 'HONEY',
    });
    setRows([{ ...EMPTY_ROW, siteId: defaultSiteId }]);
    setOpenProductSearchIndex(null);
    setShowForm(true);
  };

  const updateRow = (index: number, patch: Partial<FormRow>) => {
    setRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const handleProductSelect = (index: number, productId: number) => {
    const product = meta?.products.find((item) => item.id === productId);
    if (!product) return;
    updateRow(index, {
      productId,
      productSearch: productOptionLabel(product),
      uomItemId: product.defaultUomItemId ?? '',
      waterPercent: '',
    });
    setOpenProductSearchIndex(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(EMPTY_FORM);
    setRows([]);
    setOpenProductSearchIndex(null);
  };

  const save = async () => {
    const payload: PurchaseDocumentV2FormData = {
      ...form,
      rows: rows
        .filter((row) => row.productId !== '' && row.qty.trim() !== '')
        .map((row) => ({
          id: row.rowId ?? null,
          containerCode: row.containerCode.trim() || null,
          productId: Number(row.productId),
          qty: Number(normalizeLocalizedDecimal(row.qty)),
          uomItemId: row.uomItemId === '' ? null : Number(row.uomItemId),
          siteId: row.siteId === '' ? null : Number(row.siteId),
          waterPercent: row.waterPercent.trim() === '' ? null : Number(normalizeLocalizedDecimal(row.waterPercent)),
          notes: row.notes.trim() || null,
        })),
    };

    setSaving(true);
    try {
      const created = await purchaseDocumentsV2Api.create(payload);
      toast.success('Inköpsorder skapad');
      closeForm();
      await loadAll();
      navigate(`/purchases/${created.id}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara inköpsorder');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !meta) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Inköpsordrar</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedYear((year) => year - 1)}
            aria-label="Föregående år"
            className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
          >
            ←
          </button>
          <div className="min-w-[64px] text-center text-sm font-semibold">{selectedYear}</div>
          <button
            type="button"
            onClick={() => setSelectedYear((year) => year + 1)}
            aria-label="Nästa år"
            disabled={selectedYear >= currentYear}
            className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white"
          >
            →
          </button>
        </div>
        <WithWrite moduleCode="receiving">
          <button type="button" onClick={openCreate} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover">
            Ny inköpsorder
          </button>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Sök inköp"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="w-56"
        />
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="w-48">
          <option value="">Alla typer</option>
          {meta.purchaseTypes.map((item) => (
            <option key={item.value} value={item.value}>
              {purchaseTypeLabel(item.value, item.label)}
            </option>
          ))}
        </select>
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Inköpsorder</th>
            <th className="px-2 py-1.5 text-left font-medium">Typ</th>
            <th className="px-2 py-1.5 text-left font-medium">Leverantör</th>
            <th className="px-2 py-1.5 text-left font-medium">Datum</th>
            <th className="px-2 py-1.5 text-left font-medium">Rader</th>
            <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
          </tr>
          <FilterRow>
            <td colSpan={6} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filteredDocuments.map((document) => (
            <tr
              key={document.id}
              onClick={() => navigate(`/purchases/${document.id}`)}
              className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
            >
              <td className="px-2 py-1 font-medium">{document.documentNumber}</td>
              <td className="px-2 py-1">
                {purchaseTypeLabel(
                  document.purchaseType,
                  meta.purchaseTypes.find((item) => item.value === document.purchaseType)?.label ?? null
                )}
              </td>
              <td className="px-2 py-1">{document.supplierName ?? '-'}</td>
              <td className="px-2 py-1">{formatDateOnly(document.documentDate)}</td>
              <td className="px-2 py-1">{document.rowCount}</td>
              <td className="px-2 py-1">{formatQty(document.totalQty)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrapper>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div>
                <h2 className="text-base font-semibold">Ny inköpsorder</h2>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div>
                  <label className="block text-sm text-gray-600">Inköpsnummer</label>
                  <input
                    value={nextDocumentNumber}
                    readOnly
                    className="w-1/2 bg-gray-100 text-center text-lg font-semibold text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Typ *</label>
                  <select
                    value={form.purchaseType}
                    onChange={(event) => setForm((current) => ({ ...current, purchaseType: event.target.value }))}
                    className="w-full"
                  >
                    {meta.purchaseTypes.map((item) => (
                      <option key={item.value} value={item.value}>
                        {purchaseTypeLabel(item.value, item.label)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Leverantör</label>
                  <select
                    value={form.supplierId ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, supplierId: event.target.value ? Number(event.target.value) : null }))}
                    className="w-full"
                  >
                    <option value="">-</option>
                    {meta.suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Datum *</label>
                  <input
                    type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD"
                    value={form.documentDate}
                    onChange={(event) => setForm((current) => ({ ...current, documentDate: event.target.value }))}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium">Rader</h3>
                  <button
                    type="button"
                    onClick={() => setRows((current) => [...current, { ...EMPTY_ROW, siteId: defaultSiteId }])}
                    className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
                  >
                    Lägg till rad
                  </button>
                </div>
                <div className="space-y-2">
                  {rows.map((row, index) => {
                    const selectedUomLabel = meta.uoms.find((uom) => uom.id === (row.uomItemId === '' ? null : row.uomItemId))?.label ?? null;
                    const showBulkFields =
                      purchaseTypeNeedsQualityFields(form.purchaseType) || isBulkHoneyOrPollen(meta, row.productId);
                    const matchingProducts = meta.products
                      .filter((product) => {
                        const search = row.productSearch.trim().toLowerCase();
                        if (!search) return true;
                        return `${product.sku ?? ''} ${product.name}`.toLowerCase().includes(search);
                      })
                      .slice(0, 12);

                    return (
                      <div key={index} className="grid grid-cols-12 gap-2 rounded border border-gray-200 p-3">
                        <div className="col-span-12 md:col-span-1">
                          <label className="block text-sm text-gray-600">BatchNr</label>
                          <div className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm font-medium text-gray-700">
                            {formatContainerDisplayId(row.containerId, row.containerCode) || 'Skapas automatiskt'}
                          </div>
                        </div>
                        <div className="col-span-12 md:col-span-4">
                          <label className="block text-sm text-gray-600">Produkt</label>
                          <div className="relative">
                            <input
                              value={row.productSearch}
                              placeholder="Sök artikelnummer eller namn"
                              onFocus={() => setOpenProductSearchIndex(index)}
                              onChange={(event) => updateRow(index, { productId: '', productSearch: event.target.value })}
                              onBlur={() => {
                                window.setTimeout(() => {
                                  setOpenProductSearchIndex((current) => (current === index ? null : current));
                                  setRows((current) =>
                                    current.map((entry, rowIndex) => {
                                      if (rowIndex !== index || entry.productId === '') return entry;
                                      const product = meta.products.find((candidate) => candidate.id === Number(entry.productId));
                                      return product ? { ...entry, productSearch: productOptionLabel(product) } : entry;
                                    })
                                  );
                                }, 120);
                              }}
                              className="w-full"
                            />
                            {openProductSearchIndex === index && (
                              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                                {matchingProducts.map((product) => (
                                  <button
                                    key={product.id}
                                    type="button"
                                    onMouseDown={(event) => {
                                      event.preventDefault();
                                      handleProductSelect(index, product.id);
                                    }}
                                    className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                  >
                                    {productOptionLabel(product)}
                                  </button>
                                ))}
                                {matchingProducts.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-span-6 md:col-span-1">
                          <label className="block text-sm text-gray-600">Mängd</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={row.qty}
                            onKeyDown={(event) => {
                              if (isPieceUom(selectedUomLabel) && (event.key === '.' || event.key === ',')) {
                                event.preventDefault();
                              }
                            }}
                            onChange={(event) => updateRow(index, { qty: event.target.value })}
                            className="w-full"
                          />
                        </div>
                        <div className="col-span-6 md:col-span-1">
                          <label className="block text-sm text-gray-600">Enhet</label>
                          <select
                            value={row.uomItemId}
                            onChange={(event) => updateRow(index, { uomItemId: event.target.value ? Number(event.target.value) : '' })}
                            className="w-full"
                          >
                            <option value="">-</option>
                            {meta.uoms.map((uom) => (
                              <option key={uom.id} value={uom.id}>
                                {uom.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-12 md:col-span-2">
                          <label className="block text-sm text-gray-600">Lagerplats</label>
                          <select
                            value={row.siteId}
                            onChange={(event) => updateRow(index, { siteId: event.target.value ? Number(event.target.value) : '' })}
                            className="w-full"
                          >
                            <option value="">Välj lagerplats</option>
                            {meta.sites.map((site) => (
                              <option key={site.id} value={site.id}>
                                {site.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {showBulkFields && (
                          <div className="col-span-12 md:col-span-1">
                            <label className="block text-sm text-gray-600">Vattenhalt %</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={row.waterPercent}
                              onChange={(event) => updateRow(index, { waterPercent: event.target.value })}
                              className="w-full"
                            />
                          </div>
                        )}
                        <div className="col-span-12 md:col-span-2 flex items-end justify-end">
                          <button
                            type="button"
                            onClick={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                            className="rounded border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50"
                          >
                            Ta bort
                          </button>
                        </div>
                        <div className="col-span-12">
                          <label className="block text-sm text-gray-600">Anteckningar</label>
                          <textarea
                            value={row.notes}
                            onChange={(event) => updateRow(index, { notes: event.target.value })}
                            className="min-h-[72px] w-full rounded border border-gray-300 px-3 py-2"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea
                  value={form.notes ?? ''}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                  className="min-h-[100px] w-full"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button type="button" onClick={closeForm} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

